import React, { useMemo } from "react";
import BaseNode from "./BaseNode";
import type { HandleProps, NodeProps } from "./types";
import { Position } from "@xyflow/react";
import { BrainCircuitIcon } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import FormField from "../FormField";
import RichInput from "../RichInput";
import useNodeField from "@/hooks/useNodeField";
import useHandles from "@/hooks/useHandles";
import OpenAIIcon from "../icons/OpenAIIcon";

interface OpenAIData {
  prompt?: string;
  model?: string;
  system?: string;
  onChange: (data: Partial<OpenAIData>) => void;
}

const OpenAINode: React.FC<NodeProps> = (props) => {
  const openaiData = props.data as OpenAIData;

  const [prompt, setPrompt] = useNodeField<string>(
    props.id,
    "prompt",
    openaiData.prompt ?? "",
  );

  const [model, setModel] = useNodeField<string>(
    props.id,
    "model",
    openaiData.model ?? "gpt-4o",
  );

  const [system, setSystem] = useNodeField<string>(
    props.id,
    "system",
    openaiData.system ?? "",
  );

  const defaultHandles: HandleProps[] = useMemo(
    () => [
      {
        type: "target",
        position: Position.Left,
        id: "system",
        label: "system",
      },
      {
        type: "target",
        position: Position.Left,
        id: "prompt",
        label: "prompt",
      },
      {
        type: "source",
        position: Position.Right,
        id: "output",
        label: "output",
      },
    ],
    [],
  );

  const { handles, onVariablesChange } = useHandles();

  const allHandles = useMemo(() => {
    return [...defaultHandles, ...handles];
  }, [defaultHandles, handles]);

  return (
    <BaseNode
      title="OpenAI"
      icon={<OpenAIIcon />}
      handles={allHandles}
      className="w-[300px]"
      {...props}
    >
      <FormField label="System">
        <RichInput
          id={`${props.id}-system`}
          value={system}
          onChange={setSystem}
          onVariablesChange={onVariablesChange}
        />
      </FormField>
      <FormField label="Prompt">
        <RichInput
          id={`${props.id}-prompt`}
          value={prompt}
          onChange={setPrompt}
          onVariablesChange={onVariablesChange}
        />
      </FormField>
      <FormField label="Model">
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Theme" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gpt-4o">gpt-4o</SelectItem>
            <SelectItem value="gpt-4">gpt-4</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
    </BaseNode>
  );
};

export default OpenAINode;
